import React, { useEffect, useState, useRef } from "react";
import "./css/add_account.scss";

import { residence_total_payment } from "../../../calculations/debt_info";

import LIABILITY_TYPES, {
  PRIMARY_RESIDENCE,
  SECOND_HOME,
  SECOND_LOAN_SUB,
  HOME_EQUITY,
  MORTGAGE,
  CURRENT_HOME_EXP
} from "../../../constants/liability_types";
import { buildPropertyList } from "../../../constants/property_list";
import LIEN_POSITIONS from "../../../constants/lien_positions";

// Liability types that are secured by a property, so the LO can attach them.
const MORTGAGE_ATTACH_TYPES = [
  SECOND_HOME,
  SECOND_LOAN_SUB,
  HOME_EQUITY,
  MORTGAGE,
  CURRENT_HOME_EXP
];
import generate_id from "../../../constants/id-generator";

import CurrencyField from "../../../components/fields/currency_field";
import SelectField from "../../../components/fields/select_field";
import TextField from "../../../components/fields/text_field";
import CheckboxField from "../../../components/fields/checkbox_field";
import { getOwners, withOwners } from "../../../constants/debt_owners";

import ResidenceReserveCalculator from "./residence_reserve_calculator";
import { useDocument } from "../../../components/loan_tools/loan_tool";
import Section from "../../../components/ui/section";
import { INCLUDE_IN_DTI } from "../../../constants/account_dispositions";
import { ACCOUNT_ID_PREFIX, RESIDENCE_ID_PREFIX } from "../../../constants/debt-info";
import { default_debt_info_account, default_debt_info_residence } from "../../../schema/debt_info";

export default function DebtInfoAddAccount({
  editAccountId,
  showAccountTable
}) {
  const [document, setDocument, , { saveDocument }] = useDocument();
  const { accountSummaries, residences, applicant, loans } = document;
  const { borrowers } = applicant;
  const [account, setAccount] = useState({
    ...default_debt_info_account,
    id: generate_id(ACCOUNT_ID_PREFIX)
  });
  const [residence, setResidence] = useState({
    ...default_debt_info_residence,
    id: generate_id(RESIDENCE_ID_PREFIX)
  });
  const {
    liability_type,
    description,
    account_number,
    monthly_payment,
    current_balance,
    high_credit_limit
  } = account;
  const callback = useRef();

  // update callback with current state values
  useEffect(() => {
    callback.current = () => {
      if (getOwners(account).length > 0 && account.liability_type) {
        if (account.is_modified || residence.is_modified) {
          if (
            window.confirm(
              "You have unsaved data in account form. Would you like to save it before you leave?"
            )
          ) {
            save();
          } else {
            // do nothing
          }
        }
      } else {
        alert(
          "Not all necessary fields were entered. Therefore changes would be dismissed."
        );
      }
    };
  }, [account, residence]);

  useEffect(() => {
    //do nothing

    //cleanup
    return () => {
      callback.current();
    };
  }, []);

  const isResidence = needsResidenceInformation();
  const monthlyPayment = isResidence
    ? residence_total_payment(
      residence.first_mortgage,
      residence.second_mortgage,
      residence.taxes,
      residence.insurance,
      residence.hoa
    ).toString()
    : monthly_payment;

  const currentBalance = isResidence
    ? residence.principal_balance
    : current_balance;

  useEffect(() => {
    const accountToEdit = accountSummaries[editAccountId];
    if (editAccountId && accountToEdit) {
      setAccount(accountToEdit);
      if (accountToEdit.residence) {
        setResidence(residences[accountToEdit.residence]);
      }
    }
  }, []);

  function toggleOwner(index, checked) {
    const current = getOwners(account);
    const key = String(index);
    let next;
    if (checked) {
      next = current.includes(key) ? current : [...current, key];
    } else {
      next = current.filter((i) => i !== key);
    }
    // Keep owners in borrower order so the primary (owners[0]) is stable.
    next.sort((a, b) => parseInt(a, 10) - parseInt(b, 10));
    updateAccount(withOwners(account, next));
  }

  function renderOwnerCheckbox(borrower, index) {
    const borrowerName = `${borrower.first_name} ${borrower.last_name}`.trim();
    return (
      <CheckboxField
        key={index}
        id={`owner_${index}`}
        name={borrowerName || `Borrower ${index + 1}`}
        checked={getOwners(account).includes(String(index))}
        fieldName="checked"
        onChange={({ checked }) => toggleOwner(index, checked)}
      />
    );
  }

  function needsResidenceInformation() {
    const liabilityTypes = [PRIMARY_RESIDENCE, SECOND_HOME];
    return liabilityTypes.includes(account.liability_type);
  }

  // A mortgage/lien can be secured by one or more of the borrower's properties.
  const canAttachProperties = MORTGAGE_ATTACH_TYPES.includes(
    account.liability_type
  );
  const propertyList = buildPropertyList(document);

  function toggleAttachedProperty(propertyKey, checked) {
    const current = Array.isArray(account.attached_properties)
      ? account.attached_properties
      : [];
    const next = checked
      ? current.includes(propertyKey)
        ? current
        : [...current, propertyKey]
      : current.filter(k => k !== propertyKey);
    updateAccount({ attached_properties: next });
  }

  function updateAccount(newAccountState) {
    const accountEdit = {
      ...account,
      ...newAccountState,
      is_modified: true
    };
    setAccount(accountEdit);
  }

  function updateResidence(newResidence) {
    const residenceEdit = { ...residence, ...newResidence, is_modified: true };
    account.monthly_payment = residence_total_payment(
      residenceEdit.first_mortgage,
      residenceEdit.second_mortgage,
      residenceEdit.taxes,
      residenceEdit.insurance,
      residenceEdit.hoa
    ).toString();
    account.current_balance = residenceEdit.principal_balance;
    setAccount(account);
    setResidence(residenceEdit);
  }
  function save() {
    const isResidence = needsResidenceInformation();
    delete account.is_modified;
    delete residence.is_modified;

    if (isResidence) {
      account.residence = residence.id;
      residences[residence.id] = residence;
    } else {
      delete residences[account.residence];
      delete account.residence;
    }

    const accountSummariesEdit = {
      ...accountSummaries,
      [account.id]: account
    };

    const loansEdit = loans.map(loan => {
      const updatedLoan = { ...loan };
      updatedLoan.account_dispositions = {
        [account.id]: INCLUDE_IN_DTI,
        ...loan.account_dispositions
      };
      return updatedLoan;
    });

    setDocument({
      ...document,
      accountSummaries: accountSummariesEdit,
      loans: loansEdit,
      residences
    });
    saveDocument();
    showAccountTable();
  }

  return (
    <div className="debt-info-add-account">
      <Section name={editAccountId ? "Edit Account" : "Add Account"}>
        <div className="half-width">
          <div className="owner-checkboxes">
            <div className="owner-checkboxes-label">
              Borrower(s) responsible
              <span className="owner-checkboxes-hint">
                {" "}
                — check each owner; two or more = joint account
              </span>
            </div>
            {borrowers.map(renderOwnerCheckbox)}
          </div>

          <SelectField
            id="liability_type"
            name="Liability Type"
            value={liability_type}
            fieldName="liability_type"
            onChange={updateAccount}
            selectOptions={LIABILITY_TYPES}
          />

          {canAttachProperties && (
            <SelectField
              id="lien_position"
              name="Lien Position"
              value={account.lien_position}
              fieldName="lien_position"
              onChange={updateAccount}
              selectOptions={LIEN_POSITIONS}
              title="LANIS Training Tip: The lien's position against the property — 1st, 2nd, 3rd, or Other Lien (PACE / energy-efficient, HOA super-lien). This feeds the Loan Calculator's Current Loan Balance by position and the CLTV/TLTV once the flow is wired."
            />
          )}

          {canAttachProperties && (
            <div className="attach-property">
              <div className="attach-property-label">
                Attach to property — check each property this loan is secured by
              </div>
              {propertyList.length === 0 ? (
                <div className="attach-property-empty">
                  No properties yet. Add them on the Applicant Info (owned home)
                  or Invest REO page.
                </div>
              ) : (
                propertyList.map(p => (
                  <CheckboxField
                    key={p.key}
                    id={`attach_${p.key}`}
                    name={`${p.label} — ${p.sublabel}`}
                    checked={(account.attached_properties || []).includes(p.key)}
                    fieldName="checked"
                    onChange={({ checked }) =>
                      toggleAttachedProperty(p.key, checked)
                    }
                  />
                ))
              )}
            </div>
          )}

          <TextField
            id="account_description"
            name="Company"
            value={description}
            fieldName="description"
            onChange={updateAccount}
          />

          <TextField
            id="account_number"
            name="Account number"
            value={account_number}
            fieldName="account_number"
            onChange={updateAccount}
            title="LANIS Training Tip: Only use the last 4 digits of the account number and cross reference it to the credit report."
          />

          <CurrencyField
            id="monthly_payment"
            name="Monthly Payment"
            readOnly={isResidence}
            value={monthlyPayment}
            fieldName="monthly_payment"
            onChange={updateAccount}
          />

          <CurrencyField
            name="Current Balance"
            value={currentBalance}
            readOnly={isResidence}
            fieldName="current_balance"
            onChange={updateAccount}
          />

          <CurrencyField
            name="High Credit Limit"
            value={high_credit_limit}
            fieldName="high_credit_limit"
            onChange={updateAccount}
          />

          {needsResidenceInformation() && (
            <ResidenceReserveCalculator
              residence={residence}
              onChange={updateResidence}
            />
          )}
        </div>

        <div>
          <button className="button" onClick={showAccountTable}>
            Cancel
          </button>
          <button className="button" onClick={save}>
            Save
          </button>
        </div>
      </Section>
    </div>
  );
}
